import React from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import get from "lodash/get";
import { collectionToStories } from "@quintype/components";
import CollectionTitle from "../../atoms/CollectionTitle";
import { useDeviceType } from "../../custom-hooks/useDeviceType";
import { RowSeparator } from "../../atoms/RowSeparator";
import { DfpComponent } from "../../ads/dfp-component";
import { OtherStories } from "./OtherStories";
import { StoryCard } from "./StoryCard";
import Newsletter from "../../atoms/Newsletter";
import { BreadCrumbs } from "../../story-templates/atoms";

import "./three-col-five-stories.m.css";

export default function ThreeColumnFiveStories({ collection, extraProp = "", customSectionTemplate = false }) {
  const associatedMetadata = get(collection, ["associated-metadata"], {});

  const showTimeStamp = associatedMetadata.show_timestamp || null;
  const showReadTime = associatedMetadata.show_read_time || null;
  const showSubheadline = associatedMetadata.show_sub_headline || null;
  const showRowTitle = associatedMetadata.show_row_title || null;
  const showColumnSeparator = associatedMetadata.show_column_separator || null;
  const showStorySeparator = associatedMetadata.show_story_separator || null;
  const showRowSeparator = associatedMetadata.show_row_separator || null;
  const rowAdDetails = associatedMetadata.row_ad_unit_details || null;
  const newsLetterAdDetails = associatedMetadata.news_letter_ad_details || null;
  const enableSponsorAd = associatedMetadata.enable_sponsor_ad || null;
  const sponsorAdDetails = associatedMetadata.sponsor_ad_details || null;
  const rowAdUnitBottom = associatedMetadata.show_row_ad_unit_bottom || null;

  const deviceType = useDeviceType();
  const isMobile = deviceType === "mobile";
  const isTablet = deviceType === "tablet";
  const sectionSlug = extraProp;
  const isDesktop = deviceType === "desktop";
  const enableNewsLetter = (associatedMetadata.enable_news_letter && isDesktop) || null;

  const stories = collectionToStories(collection);

  if (!stories.length) return null;

  const [firstStory, ...otherStories] = stories;

  const getColStyle = (separatorRule, styleRule) => {
    const rule = isMobile ? showStorySeparator : separatorRule;
    const styleRuleWithSeparator = `${styleRule}-with-separator`;
    return rule ? styleRuleWithSeparator : styleRule;
  };
  const bottomAdConfig = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "slots", "row_bottom_ad"], {})
  );
  const sponsorAdConfig = useSelector((state) => get(state, ["qt", "config", "ads-config", "slots", "sponsor_ad"], {}));
  const pageType = useSelector((state) => get(state, ["qt", "pageType"], null));
  const desktopAdDim = enableNewsLetter ? { width: 300, height: 250 } : { width: 300, height: 600 };
  const rowAd = isMobile ? { width: 300, height: 250 } : isTablet ? { width: 300, height: 600 } : desktopAdDim;
  const rowAdStyle = "three-col-five-ad-slot-size-" + rowAd.width + "x" + rowAd.height;
  const rowAdConfig1 = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "slots", `row_ad_story_300x600`], {})
  );
  const rowAdConfig2 = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "slots", `row_ad_story_300x250`], {})
  );
  const currPath = useSelector((state) => get(state, ["qt", "currentPath"], ""))
    .split("?")[0]
    .split("/")[1];
  const pageName = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "ad_name_mapping", currPath], "sections")
  );
  const networkId = useSelector((state) => get(state, ["qt", "config", "ads-config", "network_id"], ""));
  const rightTopAdUnit = `/${networkId}/GULFNEWS/Gulfnews_ATF/gulfnews_${pageName}_atf/gulfnews_${pageName}_right_atf`;
  const rightBottomAdUnit = `/${networkId}/GULFNEWS/Gulfnews_BTF/gulfnews_${pageName}_btf/gulfnews_${pageName}_btf_right`;
  const bottomAdUnit = `/${networkId}/GULFNEWS/Gulfnews_BTF/gulfnews_${pageName}_btf/gulfnews_${pageName}_btf_footer`;
  const adConfig1 = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "slots", "section_page_adslot_1"], {})
  );
  const adConfig2 = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "slots", "section_page_adslot_2"], {})
  );
  const adConfig3 = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "slots", "section_page_adslot_3"], {})
  );
  const removeLeadingSlash = (path) => {
    return path.startsWith("/") ? path.slice(1) : path;
  };

  let currentPath = "";
  if (typeof window !== "undefined" && window.location) {
    currentPath = removeLeadingSlash(window.location.pathname);
  }

  const getColumn3 = () => {
    return (
      <div styleName="column-three">
        <>
          <div styleName={`ad-slot`}>
            {(isDesktop && !enableNewsLetter) || isTablet ? (
              <DfpComponent
                adStyleName={rowAdStyle}
                id={`${pageType}-${collection.slug}-row-ad1`}
                path={rightTopAdUnit}
                size={rowAdConfig1.sizes}
                viewPortSizeMapping={rowAdConfig1.view_port_size_mapping}
              />
            ) : (
              <DfpComponent
                adStyleName={rowAdStyle}
                id={`${pageType}-${collection.slug}-row-ad2`}
                path={rightBottomAdUnit}
                size={rowAdConfig2.sizes}
                viewPortSizeMapping={rowAdConfig2.view_port_size_mapping}
              />
            )}
          </div>
          {enableNewsLetter && <Newsletter customStyle={{ marginTop: "16px" }} />}
        </>
      </div>
    );
  };
  return (
    <div className="row-component" styleName={`top-component-wrapper ${customSectionTemplate ? "divider-bottom" : ""}`}>
      <div>{customSectionTemplate && <BreadCrumbs currentPath={currentPath} />}</div>
      <div styleName="three-col-five-stories">
        {showRowTitle && (
          <CollectionTitle
            collection={collection}
            enableSponsorAd={enableSponsorAd}
            sponsorAdDetails={sponsorAdDetails}
            sectionSlug={sectionSlug}
            sponsorAdConfig={sponsorAdConfig}
          />
        )}
        <div styleName={getColStyle(showColumnSeparator, "stories-list")}>
          <div styleName="column-one">
            <StoryCard
              story={firstStory}
              showTimeStamp={showTimeStamp}
              showReadTime={showReadTime}
              isHeroStory={true}
              showSubheadline={showSubheadline}
              sectionSlug={sectionSlug}
              isMobile={isMobile}
            />
          </div>
          {isMobile
            ? showStorySeparator && otherStories.length > 0 && <div styleName="divider"></div>
            : showColumnSeparator && otherStories.length > 0 && <div styleName="divider"></div>}
          {isMobile ? (
            otherStories.length > 0 && (
              <div styleName={getColStyle(showStorySeparator, "column-two-mobile")}>
                <OtherStories
                  otherStories={otherStories}
                  showStorySeparator={showStorySeparator}
                  showTimeStamp={showTimeStamp}
                  showReadTime={showReadTime}
                  isFirstSet={true}
                  sectionSlug={sectionSlug}
                />
              </div>
            )
          ) : (
            <div styleName={getColStyle(showStorySeparator, "column-two")}>
              <OtherStories
                otherStories={otherStories}
                showStorySeparator={showStorySeparator}
                showTimeStamp={showTimeStamp}
                showReadTime={showReadTime}
                isFirstSet={true}
                sectionSlug={sectionSlug}
              />
            </div>
          )}
          {isMobile
            ? showStorySeparator && otherStories.length > 0 && <div styleName="tablet-divider"></div>
            : isTablet
            ? showStorySeparator && <div styleName="tablet-divider"></div>
            : showColumnSeparator && <div styleName="tablet-divider"></div>}
          {!isTablet && getColumn3()}
        </div>
        {isTablet && <div styleName={getColStyle(showStorySeparator, "tablet-row-two")}>{getColumn3()}</div>}
      </div>
      {showRowSeparator && !customSectionTemplate && <RowSeparator />}
      {rowAdUnitBottom && (
        <DfpComponent
          adStyleName="ad-slot-size-300x250"
          id={`${pageType}-${collection.slug}-row-bottom-ad`}
          path={bottomAdUnit}
          size={adConfig3.sizes}
          viewPortSizeMapping={adConfig3.view_port_size_mapping}
        />
      )}
    </div>
  );
}

ThreeColumnFiveStories.propTypes = {
  collection: PropTypes.shape({
    slug: PropTypes.string,
  }),
  extraProp: PropTypes.string,
};

ThreeColumnFiveStories.storyLimit = 5;
